/**
 * `/$org/settings/members` — who is in this organization. A searchable table
 * (face, name, address, role, allowance, joined) with Invite in the header;
 * a row opens the member sheet, where an admin changes the role, edits usage
 * limits and removes the membership (the API refuses to leave the
 * organization without an admin). Unanswered invitations sit underneath and
 * can be withdrawn.
 *
 * @module components/settings/MembersSettings
 */
import { ChevronRightIcon, MailIcon, SearchIcon, UserPlusIcon } from "lucide-react";
import { useMemo, useState } from "react";

import type { OrgInvitation, OrgTeamMember } from "../../state/orgProfile";
import { useOrgs } from "../../state/orgs";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { InputGroup, InputGroupAddon, InputGroupInput } from "../ui/input-group";
import { InviteMemberDialog, SignInLinkCopy } from "./InviteMemberDialog";
import { MemberAvatar } from "./MemberAvatar";
import { MemberSheet } from "./MemberSheet";
import { MemberUsageCell } from "./MemberUsage";
import {
  countAdmins,
  filterMembers,
  isAdminMember,
  memberDisplayName,
  memberJoinedDate,
  memberPositionLabel,
  sortMembers,
} from "./orgMembers.logic";
import { SettingsCard, SettingsPageHeader } from "./settingsCard";
import { SettingsPageContainer } from "./settingsLayout";
import { searchableSetting } from "./settingsSearch";
import { useOrgSettingsPage } from "./orgSettingsShared";
import { useOrgAllowances } from "./useOrgAllowances";

const NO_MEMBERS: ReadonlyArray<OrgTeamMember> = [];

function expiresLabel(expiresAt: string): string {
  const date = new Date(expiresAt);
  return Number.isNaN(date.getTime())
    ? ""
    : `Expires ${date.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;
}

export function MembersSettingsPanel() {
  const { viewer } = useOrgs();
  const {
    org,
    profile,
    canManage,
    applyMemberAction,
    invite,
    revokeInvitation,
    setMemberOnDemandUsage,
    setMemberModelAllowance,
    fallback,
  } = useOrgSettingsPage("Members");
  const team = profile?.team ?? NO_MEMBERS;
  const allowances = useOrgAllowances(org?.id ?? null, team, viewer.userId);
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);

  const rows = useMemo(
    () => sortMembers(filterMembers(team, query), viewer.userId),
    [query, team, viewer.userId],
  );

  if (fallback) return fallback;
  const admins = countAdmins(team);
  const selected = team.find((member) => member.id === selectedId) ?? null;

  return (
    <SettingsPageContainer className="gap-10">
      <SettingsPageHeader
        title="Members"
        description={`${team.length} ${team.length === 1 ? "person" : "people"} · ${admins} ${
          admins === 1 ? "admin" : "admins"
        }. Open a member to change their role, usage limits or membership.`}
        actions={
          canManage ? (
            <Button size="sm" onClick={() => setInviteOpen(true)}>
              <UserPlusIcon />
              Invite
            </Button>
          ) : null
        }
      />

      <SettingsCard
        id={searchableSetting("team-members").id}
        bodyClassName="divide-y-0"
      >
        <div className="flex items-center gap-3 border-b border-border/60 px-3 py-2.5 sm:px-4">
          <InputGroup variant="ghost" className="h-8 max-w-xs flex-1 rounded-md">
            <InputGroupAddon>
              <SearchIcon className="size-3.5" />
            </InputGroupAddon>
            <InputGroupInput
              type="search"
              value={query}
              placeholder="Search members"
              aria-label="Search members"
              onChange={(event) => setQuery(event.currentTarget.value)}
            />
          </InputGroup>
          <span className="ms-auto text-xs text-muted-foreground tabular-nums">
            {rows.length === team.length ? `${team.length}` : `${rows.length} of ${team.length}`}
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-left text-sm">
            <thead>
              <tr className="border-b border-border/60 text-xs text-muted-foreground">
                <th className="h-9 px-4 font-normal sm:px-5">Member</th>
                <th className="h-9 w-28 px-3 font-normal">Role</th>
                <th className="h-9 w-48 px-3 font-normal">Allowance</th>
                <th className="h-9 w-32 px-3 font-normal">Joined</th>
                <th className="h-9 w-10 px-3" aria-label="Open" />
              </tr>
            </thead>
            <tbody>
              {rows.map((member) => {
                const name = memberDisplayName(member);
                const position = memberPositionLabel(member);
                const isAdmin = isAdminMember(member);
                const isViewer = member.id === viewer.userId;
                return (
                  <tr
                    key={member.id}
                    data-testid="member-row"
                    className="group cursor-pointer border-b border-border/50 transition-colors last:border-b-0 hover:bg-foreground/[0.03]"
                    onClick={() => setSelectedId(member.id)}
                  >
                    <td className="px-4 py-2.5 sm:px-5">
                      <button
                        type="button"
                        className="flex w-full min-w-0 items-center gap-3 text-left outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-md"
                        aria-label={`Open ${name}`}
                        onClick={(event) => {
                          event.stopPropagation();
                          setSelectedId(member.id);
                        }}
                      >
                        <MemberAvatar member={member} />
                        <span className="min-w-0">
                          <span className="flex items-center gap-1.5">
                            <span className="truncate font-medium text-foreground">{name}</span>
                            {isViewer ? (
                              <Badge variant="secondary" size="sm">
                                You
                              </Badge>
                            ) : null}
                          </span>
                          {member.name || position ? (
                            <span className="block truncate text-xs text-muted-foreground">
                              {member.name ? member.email : position}
                            </span>
                          ) : null}
                        </span>
                      </button>
                    </td>
                    <td className="px-3 py-2.5">
                      <Badge variant={isAdmin ? "info" : "outline"}>
                        {isAdmin ? "Admin" : "Member"}
                      </Badge>
                    </td>
                    <td className="px-3 py-2.5">
                      <MemberUsageCell
                        member={member}
                        standardGbp={allowances.standardGbp}
                        spend={allowances.spendFor(member)}
                      />
                    </td>
                    <td className="px-3 py-2.5 text-[13px] whitespace-nowrap text-muted-foreground">
                      {memberJoinedDate(member.joinedAt) ?? "—"}
                    </td>
                    <td className="px-3 py-2.5 text-muted-foreground/60 group-hover:text-foreground">
                      <ChevronRightIcon className="size-4" aria-hidden />
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-5 py-10 text-center text-sm text-muted-foreground">
                    {team.length === 0
                      ? "Nobody else has joined this organization yet."
                      : `No members match “${query.trim()}”.`}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </SettingsCard>

      {canManage ? (
        <InvitationsCard
          invitations={profile!.invitations}
          emailEnabled={profile!.invitationEmailEnabled}
          revokeInvitation={revokeInvitation}
          onInvite={() => setInviteOpen(true)}
        />
      ) : null}

      <InviteMemberDialog
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        emailEnabled={profile!.invitationEmailEnabled}
        invite={invite}
      />

      <MemberSheet
        member={selected}
        onClose={() => setSelectedId(null)}
        viewerUserId={viewer.userId}
        canManage={canManage}
        isOperator={viewer.isGlobalAdmin}
        standardGbp={allowances.standardGbp}
        overageMultiplier={allowances.overageMultiplier}
        spendFor={allowances.spendFor}
        applyMemberAction={applyMemberAction}
        onToggleOnDemand={setMemberOnDemandUsage}
        onSetAllowance={setMemberModelAllowance}
      />
    </SettingsPageContainer>
  );
}

function InvitationsCard({
  invitations,
  emailEnabled,
  revokeInvitation,
  onInvite,
}: {
  readonly invitations: ReadonlyArray<OrgInvitation>;
  readonly emailEnabled: boolean;
  readonly revokeInvitation: (invitationId: string) => Promise<string | null>;
  readonly onInvite: () => void;
}) {
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const revoke = (invitation: OrgInvitation) => {
    setBusyId(invitation.id);
    setError(null);
    void revokeInvitation(invitation.id).then((failure) => {
      setBusyId(null);
      setError(failure);
    });
  };

  return (
    <SettingsCard
      id={searchableSetting("team-invitations").id}
      title="Pending invitations"
      description={
        emailEnabled
          ? "Invitees join by signing in with the invited address. Invitations last two weeks."
          : "Invitees join by signing in with the invited address through the sign-in link. Invitations last two weeks."
      }
      footer={
        error ? (
          <p role="alert" className="text-xs text-destructive">
            {error}
          </p>
        ) : null
      }
    >
      {invitations.length === 0 ? (
        <div className="flex items-center gap-3 px-4 py-4 sm:px-5">
          <span className="flex size-8 shrink-0 items-center justify-center rounded-full border border-dashed border-border text-muted-foreground">
            <MailIcon className="size-3.5" aria-hidden />
          </span>
          <p className="min-w-0 flex-1 text-[13px] text-muted-foreground">
            No invitations waiting for an answer.
          </p>
          <Button size="xs" variant="ghost-muted" onClick={onInvite}>
            Invite someone
          </Button>
        </div>
      ) : (
        <>
          {invitations.map((invitation) => (
            <div key={invitation.id} className="flex items-center gap-3 px-4 py-3 sm:px-5">
              <span className="flex size-8 shrink-0 items-center justify-center rounded-full border border-dashed border-border text-muted-foreground">
                <MailIcon className="size-3.5" aria-hidden />
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm text-foreground">{invitation.email}</p>
                <p className="truncate text-xs text-muted-foreground">
                  {[
                    invitation.invitedByEmail ? `Invited by ${invitation.invitedByEmail}` : null,
                    expiresLabel(invitation.expiresAt),
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
              </div>
              <Badge variant="outline">{invitation.role === "admin" ? "Admin" : "Member"}</Badge>
              <Button
                size="xs"
                variant="ghost-muted"
                disabled={busyId === invitation.id}
                onClick={() => revoke(invitation)}
              >
                Withdraw
              </Button>
            </div>
          ))}
          {emailEnabled ? null : (
            <div className="space-y-1.5 bg-muted/30 px-4 py-3 sm:px-5">
              <p className="text-xs text-muted-foreground">Sign-in link for invitees</p>
              <SignInLinkCopy />
            </div>
          )}
        </>
      )}
    </SettingsCard>
  );
}
